import {CARRIERS, SERVICES, SERVICE_INFO} from '../../../../../_gori/constants'
import {OrdersConfig} from '../../core/_const'

const EXCLUDED_FROM_HIGHLIGHTED_SERVICES = [
  SERVICES.USPS_MEDIA,
  SERVICES.USPS_BOUND_PRINTED_MATTER,
  SERVICES.USPS_FIRSTCLASS,
]

/**
 * Maps a carrier name to its corresponding settings key.
 * "dhlecom" (DHL eCommerce) and "dhl" (DHL Express) are separate carriers
 * internally, but the carrier settings UI exposes a single "dhl" toggle that
 * controls both. We map dhlecom → dhl when looking up the settings key.
 * The reverse (dhl → dhlecom) is not needed because "dhl" is its own key.
 */
export const getCarrierSettingsKey = (carrierName: string): string =>
  carrierName === 'dhlecom' ? 'dhl' : carrierName

/** Per-carrier rate request timeout in milliseconds */
export const RATE_REQUEST_TIMEOUT = 30000

/**
 * Returns `true` when an Axios error was caused by a request timeout
 * (either the Axios `timeout` option or an `AbortError`).
 */
export const isTimeoutError = (error: any): boolean => {
  if (error?.code === 'ECONNABORTED') return true
  if (error?.message?.toLowerCase?.().includes?.('timeout')) return true
  if (error?.name === 'AbortError') return true
  return false
}

const RATE_CARRIER_PREFIXES = Array.from(new Set(['dhlecom', ...Object.values(CARRIERS)])).sort(
  (a, b) => b.length - a.length
)

export const getCarrierNameFromServiceKey = (serviceKey: string) => {
  const matchedPrefix = RATE_CARRIER_PREFIXES.find((prefix) => {
    return serviceKey === prefix || serviceKey.startsWith(`${prefix}_`)
  })

  return matchedPrefix || serviceKey.split('_')[0]
}

export const getCarrierNamesFromServices = (carriers: any) => {
  return Array.from(
    new Set(
      Object.keys(carriers || {})
        .map(getCarrierNameFromServiceKey)
        .filter(Boolean)
    )
  )
}

export const getCarrierService = (carriers: any, carrierName: string) => {
  return Object.entries(carriers || {}).find(([key]) => {
    return getCarrierNameFromServiceKey(key) === carrierName
  })?.[1] as any
}

/**
 * Determines if an order is international based on address values.
 * Mirrors the logic in OrderChild.tsx / OrderChildModal.tsx that sets formik.values.international.
 */
export const isInternationalByAddress = (fromAddress: any, toAddress: any): boolean => {
  const toCountry = toAddress?.to_country?.trim()
  const toState = toAddress?.to_state?.trim()
  const toCity = toAddress?.to_city?.trim()
  const fromCountry = fromAddress?.from_country?.trim()
  const fromState = fromAddress?.from_state?.trim()
  const fromCity = fromAddress?.from_city?.trim()

  if ((toCountry && toCountry !== 'US') || (fromCountry && fromCountry !== 'US')) return true
  if (
    OrdersConfig.US_TERRITORIES.includes(toState) ||
    OrdersConfig.US_TERRITORIES.includes(fromState)
  )
    return true
  if (OrdersConfig.US_MILITARIES.includes(toCity) || OrdersConfig.US_MILITARIES.includes(fromCity))
    return true
  return false
}

export const buildRatePayload = (formikValues: any, dataParcels: any, carrierName?: string) => ({
  shipment: {
    ...(carrierName ? {carrier: carrierName} : {}),
    international_option: {
      ...formikValues?.international_option,
      custom_items: formikValues?.custom_items,
    },
    service_options: {
      confirmation: formikValues?.signature_confirmation,
      insured_amount: Number(formikValues?.insurance_option?.insured_amount),
      hazmat: formikValues?.extra_service,
      insurance_type: formikValues?.insurance_option?.provider,
    },
    from_address: Object.entries(OrdersConfig.SHIPPING).reduce((fromData, [_key, value]) => {
      fromData[value.value] = formikValues?.from?.[`from_${value.value}`]
      return fromData
    }, {}),
    to_address: Object.entries(OrdersConfig.SHIPPING).reduce((toData, [_key, value]) => {
      toData[value.value] = formikValues?.to?.[`to_${value.value}`]
      return toData
    }, {}),
    parcel: dataParcels,
  },
})

export const normalizeCheapestRates = (rates: any[]) => {
  const rateOptions = (Array.isArray(rates) ? rates : []).filter(Boolean)

  // Deduplicate by carrier_service + package_type (later entries win)
  const dedupMap = new Map<string, any>()
  for (const item of rateOptions) {
    const key = `${item?.carrier_service}:${item?.package_type?.value ?? ''}`
    dedupMap.set(key, item)
  }
  const deduped = Array.from(dedupMap.values())

  const validRateOptions = deduped.filter((item) => {
    return !item?.error && Number.isFinite(Number(item?.rate))
  })
  const cheapestRate = validRateOptions
    .filter((item) => !EXCLUDED_FROM_HIGHLIGHTED_SERVICES.includes(item?.carrier_service))
    .reduce((cheapest, item) => {
      if (!cheapest) return item
      return Number(item.rate) < Number(cheapest.rate) ? item : cheapest
    }, null)

  return deduped.map((item) => ({
    ...item,
    cheapest: !!cheapestRate && item === cheapestRate,
  }))
}

export const normalizeRateText = (value: any, fallback = '') => {
  if (typeof value === 'string') return value
  if (!value) return fallback
  return JSON.stringify(value)
}

type ServiceInfo = {
  deliveryWindow: string
  description: string
  minDeliveryDays?: number
  maxDeliveryDays?: number
  deliveryTimeRank?: number
}

type RateItem = {
  carrier_service: string
  rate: any
  error?: any
  matching_package?: boolean
  cheapest?: boolean
  [key: string]: any
}

type PinnedRates = {
  cheapest: RateItem | null
  fastest: RateItem | null
  best: RateItem | null
}

const getServiceInfo = (rate: RateItem): ServiceInfo | undefined => {
  return SERVICE_INFO?.[rate?.carrier_service] as ServiceInfo | undefined
}

const getRateMinDeliveryDays = (rate: RateItem): number => {
  const min = getServiceInfo(rate)?.minDeliveryDays
  return Number.isFinite(min) ? min! : Infinity
}

const getRateMaxDeliveryDays = (rate: RateItem): number => {
  const max = getServiceInfo(rate)?.maxDeliveryDays
  return Number.isFinite(max) ? max! : getRateMinDeliveryDays(rate)
}

const getRateDeliveryTimeRank = (rate: RateItem): number => {
  const info = getServiceInfo(rate)

  // Use explicit rank when set (only for time-of-day tiebreakers)
  if (Number.isFinite(info?.deliveryTimeRank)) return info!.deliveryTimeRank!

  // Compute from min/max: (min * 2) + max — lower = faster
  const min = getRateMinDeliveryDays(rate)
  const max = getRateMaxDeliveryDays(rate)
  if (Number.isFinite(min) && Number.isFinite(max)) return min * 2 + max

  return Infinity
}

const compareRateSpeed = (a: RateItem, b: RateItem): number => {
  const minA = getRateMinDeliveryDays(a)
  const minB = getRateMinDeliveryDays(b)
  if (minA !== minB) return minA - minB

  const maxA = getRateMaxDeliveryDays(a)
  const maxB = getRateMaxDeliveryDays(b)
  if (maxA !== maxB) return maxA - maxB

  return getRateDeliveryTimeRank(a) - getRateDeliveryTimeRank(b)
}

/**
 * Builds the {min, max} values for intl.formatMessage delivery window templates.
 * Reads from SERVICE_INFO only. Strings without {min}/{max} placeholders
 * simply ignore the extra values.
 */
export const getDeliveryWindowValues = (
  serviceInfo: ServiceInfo | null | undefined
): Record<string, string | number> => {
  return {
    min: serviceInfo?.minDeliveryDays ?? '',
    max: serviceInfo?.maxDeliveryDays ?? '',
  }
}

/**
 * Derives the three pinned highlight picks from a list of valid rate options.
 *
 * - cheapest: lowest rate
 * - fastest: lowest delivery window / commitment rank (ties broken by price)
 * - best: best balanced middle ground between price and speed, excluding the
 *         explicit cheapest and fastest picks when possible.
 */
export const derivePinnedRates = (rateOptions: RateItem[]): PinnedRates => {
  const valid = rateOptions.filter((item) => !item?.error && Number.isFinite(Number(item?.rate)))

  if (!valid.length) return {cheapest: null, fastest: null, best: null}

  // Services in EXCLUDED_FROM_HIGHLIGHTED_SERVICES are never pinned;
  // they will appear under "More Rates" instead.
  const highlightEligible = valid.filter(
    (item) => !EXCLUDED_FROM_HIGHLIGHTED_SERVICES.includes(item.carrier_service)
  )

  if (!highlightEligible.length) return {cheapest: null, fastest: null, best: null}

  const cheapest = highlightEligible.reduce((a, b) => (Number(a.rate) <= Number(b.rate) ? a : b))

  const fastest = highlightEligible.reduce((a, b) => {
    const speedDiff = compareRateSpeed(a, b)
    if (speedDiff !== 0) return speedDiff < 0 ? a : b

    return Number(a.rate) <= Number(b.rate) ? a : b
  })

  const sortedByPrice = [...highlightEligible].sort((a, b) => Number(a.rate) - Number(b.rate))
  const sortedBySpeed = [...highlightEligible].sort((a, b) => {
    const speedDiff = compareRateSpeed(a, b)
    if (speedDiff !== 0) return speedDiff
    return Number(a.rate) - Number(b.rate)
  })

  const priceRank = new Map<string, number>()
  const speedRank = new Map<string, number>()

  sortedByPrice.forEach((item, index) => {
    priceRank.set(item.carrier_service, index)
  })
  sortedBySpeed.forEach((item, index) => {
    speedRank.set(item.carrier_service, index)
  })

  const bestCandidates = highlightEligible.filter((item) => {
    return (
      item.carrier_service !== cheapest.carrier_service &&
      item.carrier_service !== fastest.carrier_service
    )
  })

  const best =
    bestCandidates.length > 0
      ? bestCandidates.reduce((a, b) => {
          const scoreA =
            (priceRank.get(a.carrier_service) ?? Number.MAX_SAFE_INTEGER) +
            (speedRank.get(a.carrier_service) ?? Number.MAX_SAFE_INTEGER)
          const scoreB =
            (priceRank.get(b.carrier_service) ?? Number.MAX_SAFE_INTEGER) +
            (speedRank.get(b.carrier_service) ?? Number.MAX_SAFE_INTEGER)
          if (scoreA !== scoreB) return scoreA < scoreB ? a : b

          const speedDiff = compareRateSpeed(a, b)
          if (speedDiff !== 0) return speedDiff < 0 ? a : b

          return Number(a.rate) <= Number(b.rate) ? a : b
        })
      : null

  // Avoid showing duplicate cards (e.g. cheapest === fastest)
  return {
    cheapest,
    fastest: fastest.carrier_service !== cheapest.carrier_service ? fastest : null,
    best,
  }
}

export type CarrierRouteType = 'domestic' | 'international' | 'mixed'

/**
 * Determines whether a carrier handles domestic, international, or both
 * based on the `international` flag of its services in the carriers config.
 */
export const getCarrierRouteType = (carriers: any, carrierName: string): CarrierRouteType => {
  const services = Object.values(carriers || {}).filter(
    (svc: any) => getCarrierNameFromServiceKey(svc?.carrier_service || '') === carrierName
  )

  if (!services.length) return 'mixed' // unknown carrier — don't filter

  const hasInternational = services.some((svc: any) => svc?.international === true)
  const hasDomestic = services.some((svc: any) => svc?.international === false)

  if (hasInternational && hasDomestic) return 'mixed'
  if (hasInternational) return 'international'
  return 'domestic'
}

/**
 * Returns true if the carrier is applicable for the given order type.
 * Mixed carriers are always applicable.
 */
export const isCarrierApplicableForRoute = (
  carriers: any,
  carrierName: string,
  isInternational: boolean
): boolean => {
  const routeType = getCarrierRouteType(carriers, carrierName)
  if (routeType === 'mixed') return true
  if (isInternational) return routeType === 'international'
  return routeType === 'domestic'
}
